import {
  AlertAction,
  AlertCancel,
  AlertContent,
  AlertDescription,
  AlertOverlay,
  AlertPortal,
  AlertRoot,
  AlertTitle,
  BodyText,
  Button,
  Field,
  FieldLabel,
  type ItemOptionProps,
  MultiSelectSearch,
} from "@/components/ui";

import { toast } from "@/components/ui";
import type { CommandListItemVariant } from "@/components/ui/Search/types";
import { TRACKERS } from "@/constants/Trackers";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { trackEvent } from "@/lib/tracking";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useSearch } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDebounce } from "react-use";
import type { z } from "zod";
import {
  BatchCreateMedicationStatementsWarningReason,
  type CodeableConcept,
  useBatchCreateMedications,
} from "../../network/useBatchCreateMedications";
import { useSearchDrugs } from "../../network/useMedications";
import { createMultipleMedicationsFormSchema } from "../../schemas/schemas";

interface AddMultipleMedicationsFormProps {
  closeForm: () => void;
}

interface SearchResultMetadata {
  system?: string;
  isDuplicate?: boolean;
  originalCode?: string;
}

export const AddMultipleMedicationsForm = ({
  closeForm,
}: AddMultipleMedicationsFormProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ strict: false });
  const ref = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    200,
    [searchTerm],
  );
  const [duplicateWarningItem, setDuplicateWarningItem] = useState<
    ItemOptionProps | undefined
  >(undefined);

  const form = useForm<z.infer<typeof createMultipleMedicationsFormSchema>>({
    resolver: zodResolver(createMultipleMedicationsFormSchema),
    defaultValues: {
      medications: [],
    },
  });

  const { isPending: isSavingCreation, mutate: batchCreateMedications } =
    useBatchCreateMedications({
      patientId,
      onSuccess: (response) => {
        const { successfulIds, warnings, failures } = response;

        // Show success message for successful creations
        if (successfulIds.length > 0) {
          toast.success(
            `Successfully added ${successfulIds.length} medication(s)`,
          );
        }

        // Show warnings if any
        if (warnings.length > 0) {
          for (const warning of warnings) {
            if (
              warning.warnings.includes(
                BatchCreateMedicationStatementsWarningReason.FAILED_TO_SYNC,
              )
            ) {
              toast.warning(
                "Some medications were added but failed to sync to ePrescribing system",
              );
            }
            if (
              warning.warnings.includes(
                BatchCreateMedicationStatementsWarningReason.FAILED_TO_UPDATE_LHR,
              )
            ) {
              toast.warning(
                "Some medications were added but failed to update LHR",
              );
            }
          }
        }

        // Show errors for failures
        if (failures.length > 0) {
          for (const failure of failures) {
            const medicationName =
              failure.medication.text ||
              failure.medication.coding[0]?.display ||
              "Unknown medication";
            toast.error(
              `Failed to create ${medicationName}: ${failure.failureReason}`,
            );
          }
        }

        // Close form if at least one medication was created successfully OR if there are only warnings (partial success)
        if (
          successfulIds.length > 0 ||
          (warnings.length > 0 && failures.length === 0)
        ) {
          closeForm();
        }
      },
    });

  const { isLoading: isSearchLoading, data: medicationSearchResults } =
    useSearchDrugs(debouncedSearchTerm, patientId);

  const searchResults =
    medicationSearchResults?.map((searchResult, index) => ({
      label: searchResult.drugName,
      value: `${searchResult.drugCode}-${searchResult.drugCodeSystem}-${index}`, // Create unique key
      endLabel: searchResult.personalizedInfo?.previouslyCharted
        ? "Duplicate"
        : undefined,
      variant: (searchResult.personalizedInfo?.previouslyCharted
        ? "warning"
        : "default") as CommandListItemVariant,
      metadata: {
        system: searchResult.drugCodeSystem,
        isDuplicate: searchResult.personalizedInfo?.previouslyCharted,
        originalCode: searchResult.drugCode, // Store original code for reference
      } as SearchResultMetadata,
    })) || [];

  const handleSubmit = (
    values: z.infer<typeof createMultipleMedicationsFormSchema>,
  ) => {
    trackEvent(TRACKERS.SAVE_MEDICATION);

    const medications: CodeableConcept[] = values.medications.map((med) => ({
      coding: [
        {
          system: med.system,
          code: med.code,
          display: med.display,
        },
      ],
      text: med.display,
    }));

    batchCreateMedications({
      subjectId: patientId,
      encounterId: encounterId as string | undefined,
      medications,
    });
  };

  const handleItemAdded = (item: ItemOptionProps) => {
    const metadata = item.metadata as SearchResultMetadata;
    if (metadata.isDuplicate === true) {
      setDuplicateWarningItem(item);
      return;
    }

    const currentMedications = form.getValues("medications");
    // Extract the original code from the unique value (format: "code-system-index")
    const originalCode =
      metadata.originalCode || item.value.toString().split("-")[0];
    const newMedication = {
      code: originalCode,
      display: item.label,
      system: metadata.system || "",
    };

    // Check if medication already exists
    const exists = currentMedications.some(
      (med) =>
        med.code === newMedication.code && med.system === newMedication.system,
    );

    if (!exists) {
      form.setValue("medications", [...currentMedications, newMedication]);
    }
  };

  const handleItemRemoved = (item: ItemOptionProps) => {
    const currentMedications = form.getValues("medications");
    // For selected items, the value is in format "code-system-index", so extract the code
    const originalCode = item.value.toString().split("-")[0];
    const updatedMedications = currentMedications.filter(
      (med) => med.code !== originalCode,
    );
    form.setValue("medications", updatedMedications);
  };

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  return (
    <>
      <div ref={ref} />
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-2 border border-primary p-3 rounded-md">
          <BodyText size="sm" bold>
            New Medications
          </BodyText>

          <Controller
            control={form.control}
            name="medications"
            render={({ field }) => (
              <Field>
                <FieldLabel htmlFor="medications">Medications</FieldLabel>
                {form.formState.errors.medications && (
                  <BodyText size="sm" textColor="error">
                    {form.formState.errors.medications.message}
                  </BodyText>
                )}
                <MultiSelectSearch
                  placeholder="Search Medications"
                  selectedItems={field.value?.map((med, index) => ({
                    label: `${med.code} - ${med.display}`,
                    shortenedLabel: med.display,
                    value: `${med.code}-${med.system}-${index}`, // Create unique key
                    metadata: med,
                  }))}
                  noResultsText="No medications found"
                  options={searchResults}
                  onInputChange={(text) => {
                    setSearchTerm(text);
                  }}
                  onItemAdded={handleItemAdded}
                  onItemRemoved={handleItemRemoved}
                  disabled={false}
                  minLength={2}
                  isLoading={isSearchLoading}
                  showLoadingSpinner={true}
                  openOnFocus
                  autoFocusInput
                  autoFocusInputDelay={100}
                  leftIcon={SearchIcon}
                />
              </Field>
            )}
          />

          <SubmitCancelButtons
            isSaving={isSavingCreation}
            onCancel={() => {
              trackEvent(TRACKERS.CANCEL_ADDING_NEW_MEDICATION);
              form.reset();
              closeForm();
            }}
          />
        </div>
      </form>
      <AlertRoot
        isControlled
        isOpen={Boolean(duplicateWarningItem)}
        onOpenChange={() => {}}
      >
        <AlertPortal>
          <AlertOverlay />
          <AlertContent>
            <AlertTitle>Duplicate Medication</AlertTitle>
            <AlertDescription className="text-body-sm text-primary">
              {duplicateWarningItem?.label} has been previously charted. Would
              you like to add a duplicate entry?
            </AlertDescription>
            <div className="flex gap-4 align-middle justify-end">
              <AlertCancel>
                <Button
                  onClick={() => {
                    setDuplicateWarningItem(undefined);
                  }}
                  size="sm"
                  variant="secondary"
                >
                  No
                </Button>
              </AlertCancel>
              <AlertAction>
                <Button
                  onClick={() => {
                    handleItemAdded(duplicateWarningItem as ItemOptionProps);
                    setDuplicateWarningItem(undefined);
                  }}
                  size="sm"
                  variant="primary"
                >
                  Yes
                </Button>
              </AlertAction>
            </div>
          </AlertContent>
        </AlertPortal>
      </AlertRoot>
    </>
  );
};
